import { type CandidateTab } from "./types/candidate-page";
import { type CandidateFilters } from "./types/filters";
import { type Job } from "./types/job";
import { SCORE_KEYS, type ScoreKey } from "./types/scoring";

/* The filter *rules* (which candidates a filter keeps) live in the backend:
 * vidario-backend/candidates/services/candidate_page.py (#14, ADR 0006). What
 * stays here only describes the filter set to the UI: which filters count as
 * active, and their labels. */

/** The filter set the UI should actually act on, given the job.
 *
 * A radius the job cannot support is not an *active* filter: it can't narrow
 * anything, so counting it in the "N aktive Filter" badge or rendering a
 * "≤ 250 km" chip would tell the recruiter a filter is running while they look
 * at an unfiltered list — which is precisely how issue #4 presented. Deriving
 * the badge, the chips and the list from this one value keeps all three
 * honest and in agreement. */
export function effectiveFilters(filters: CandidateFilters, job?: Job): CandidateFilters {
  if (job?.distanceAvailable !== false) return filters;
  const { maxDistanceKm: _unsupported, ...rest } = filters;
  return rest;
}

/** The filters a tab asks the server for. Aussortiert rows have only a
 * Vorauswahl and a place, so the rest would empty the tab there (#200); its
 * minimum is the Vorauswahl one, a different scale from the Gesamt-Score
 * (#201). The others stay in the URL and come back on their tabs. */
export function filtersForTab(filters: CandidateFilters, tab: CandidateTab): CandidateFilters {
  const { minVorauswahl, ...rest } = filters;
  if (tab !== "pool") return rest;
  const { maxDistanceKm } = filters;
  return {
    ...(minVorauswahl !== undefined && { minScore: minVorauswahl }),
    ...(maxDistanceKm !== undefined && { maxDistanceKm }),
  };
}

/** The whole filter set after a change made on a tab: the change covers only
 * the filters shown there, so removing a chip or resetting keeps the other
 * tabs' filters (#200, #201). */
export function withTabFilters(
  filters: CandidateFilters,
  next: CandidateFilters,
  tab: CandidateTab,
): CandidateFilters {
  const { minVorauswahl, maxDistanceKm: _shown, ...elsewhere } = filters;
  if (tab !== "pool") {
    const { minVorauswahl: _poolOnly, ...shown } = next;
    return { ...shown, ...(minVorauswahl !== undefined && { minVorauswahl }) };
  }
  const { minScore, maxDistanceKm } = next;
  return {
    ...elsewhere,
    ...(minScore !== undefined && { minVorauswahl: minScore }),
    ...(maxDistanceKm !== undefined && { maxDistanceKm }),
  };
}

/** How many set filters apply only on other tabs. The radius is shared by
 * every tab, so it never counts (#201). */
export function otherTabFilterCount(filters: CandidateFilters, tab: CandidateTab): number {
  if (tab !== "pool") return typeof filters.minVorauswahl === "number" ? 1 : 0;
  const { minVorauswahl: _shown, maxDistanceKm: _alsoShown, ...elsewhere } = filters;
  return activeFilterCount(elsewhere);
}

/** The line beside the filter chips naming those filters, or null when
 * there are none. „weitere" only when chips stand before it (#201). */
export function otherTabFilterLine(
  filters: CandidateFilters,
  tab: CandidateTab,
  afterChips: boolean,
): string | null {
  const n = otherTabFilterCount(filters, tab);
  if (n === 0) return null;
  if (tab !== "pool") return `Vorauswahl ab ${filters.minVorauswahl} wirkt nur in „Aussortiert“.`;
  const where = "in „Longlist“ und „Ausgewählte Talente“";
  if (n === 1) return `1 ${afterChips ? "weiterer " : ""}Filter wirkt nur ${where}.`;
  return `${n} ${afterChips ? "weitere " : ""}Filter wirken nur ${where}.`;
}

/** Number of constraints currently active in the filter set. Used to
 * badge the "Filter" button with a count and to decide whether to show
 * the "Alle zurücksetzen" affordance. */
export function activeFilterCount(filters: CandidateFilters): number {
  let n = 0;
  if (typeof filters.maxDistanceKm === "number") n++;
  if (typeof filters.minScore === "number") n++;
  if (filters.minSubScores) {
    for (const key of SCORE_KEYS) {
      if (typeof filters.minSubScores[key] === "number") n++;
    }
  }
  if (filters.mustHavesOnly) n++;
  if (typeof filters.salaryMin === "number" || typeof filters.salaryMax === "number") n++;
  if (filters.workLocations && filters.workLocations.length > 0) n++;
  if (filters.changeReadiness && filters.changeReadiness.length > 0) n++;
  return n;
}

/** Labels matched 1:1 to the PDF spec (Hard Skills / Erfahrung / Branche
 * / Standort). Centralised here so the Drawer, the chip bar and any
 * future debug surface stay in sync. */
export const SCORE_KEY_LABELS_DE: Record<ScoreKey, string> = {
  skills: "Hard Skills",
  experience: "Erfahrung",
  industry: "Branche",
  location: "Standort",
};
